export interface WorkPoint {
  icon: string;
  title: string;
  detail: string;
}

export interface ServiceItem {
  slug: string;
  icon: string;
  title: string;
  shortDesc: string;
  heroImage: string;
  overview: string;
  features: { icon: string; title: string; desc: string }[];
  workImages: { src: string; caption: string }[];
  process: { step: string; title: string; desc: string; note?: string }[];
  workPoints: WorkPoint[];
  faq: { q: string; a: string }[];
}

export const servicesData: ServiceItem[] = [
  {
    slug: 'lgs',
    icon: 'ri-layout-grid-line',
    title: '軽鉄下地LGS工事',
    shortDesc: '近年RC造・S造の建築物において天井・壁の下地として一般的に使用されている軽量鉄下地。耐火性・施工性に優れ、耐震天井工法やR仕様にも対応。',
    heroImage: 'https://public.readdy.ai/ai/img_res/edited_b0963daa30667fd4168e3246a20390be_75346874.jpg',
    overview: '軽鉄下地LGS（Light Gauge Steel）工事は、近年RC造・S造の建築物において、天井・壁の下地として一般的に使用されている軽量鉄下地工事です。木下地に比べて耐火性能の向上や施工期間の短縮などの利点があります。株式会社永恒では、各メーカーの耐震天井工法や難易度の高いR仕様（曲面天井・曲面壁）などにも対応可能です。大型オフィスビル・マンション・商業施設・公共施設など幅広い建築物での施工実績を有しています。',
    features: [
      { icon: 'ri-fire-line', title: '高い耐火性能', desc: '木下地に比べ不燃材料であるため、建築物の耐火性能向上に大きく貢献します。' },
      { icon: 'ri-time-line', title: '施工期間の短縮', desc: '工場生産された規格品を使用するため、現場での加工が少なく工期を短縮できます。' },
      { icon: 'ri-earthquake-line', title: '耐震天井工法対応', desc: '各メーカーの耐震天井工法に対応し、地震時の天井落下リスクを低減します。' },
      { icon: 'ri-refresh-line', title: 'R仕様（曲面）対応', desc: '難易度の高い曲面天井・曲面壁など、デザイン性の高い仕様にも対応可能です。' },
      { icon: 'ri-building-4-line', title: 'RC造・S造対応', desc: 'RC造・S造など各種構造の建築物に対応した下地組みを行います。' },
      { icon: 'ri-recycle-line', title: '環境への配慮', desc: 'リサイクル可能な鋼材を使用し、廃材を削減する環境に配慮した施工を行います。' },
    ],
    workImages: [
      { src: 'https://static.readdy.ai/image/335e3e62ea46a4ee8fffcd870552cca1/bce8e9cd968b96d5a72e5f169b7273ee.jpeg', caption: '大規模施設 — LGS下地・足場組み施工現場' },
      { src: 'https://static.readdy.ai/image/335e3e62ea46a4ee8fffcd870552cca1/b554f8c2d36b87076fbc99a30a3ec131.jpeg', caption: '大規模施設 — LGS曲面天井・R仕様施工現場' },
      { src: 'https://public.readdy.ai/ai/img_res/238b146784524dddcec848f6bc5baad8.jpg', caption: '大型オフィスビル — LGS天井下地施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20workers%20installing%20seismic%20resistant%20ceiling%20system%20earthquake%20proof%20LGS%20metal%20framing%20Asian%20male%20construction%20team%20in%20safety%20helmets%20installing%20steel%20brace%20ceiling%20structure%20modern%20building%20interior%20professional%20construction%20site%20industrial%20workspace&width=800&height=600&seq=lgs-work-cn-002&orientation=landscape', caption: '商業施設 — 耐震天井工法施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20craftsmen%20installing%20curved%20ceiling%20R%20specification%20light%20gauge%20steel%20framing%20Asian%20male%20workers%20bending%20and%20fitting%20metal%20stud%20frame%20for%20curved%20ceiling%20structure%20modern%20hotel%20lobby%20interior%20construction%20professional%20team%20complex%20curved%20design&width=800&height=600&seq=lgs-work-cn-003&orientation=landscape', caption: 'ホテルロビー — R仕様曲面天井施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20construction%20workers%20installing%20LGS%20metal%20stud%20wall%20partition%20in%20RC%20concrete%20building%20interior%20Asian%20male%20workers%20in%20safety%20helmets%20assembling%20lightweight%20steel%20wall%20frame%20system%20modern%20apartment%20renovation%20professional%20construction%20site&width=800&height=600&seq=lgs-work-cn-004&orientation=landscape', caption: 'RC造マンション — 壁下地LGS施工' },
    ],
    process: [
      {
        step: '01',
        title: '現地調査・設計確認',
        desc: '建物の構造種別（RC造・S造）・天井高・設備配管位置を確認し、設計図と照合します。',
        note: '構造体の不陸・不整合を事前に把握し、施工計画に反映します。',
      },
      {
        step: '02',
        title: '施工計画・工法選定',
        desc: '耐震工法・R仕様・防音仕様など要件を確定し、使用部材・施工手順を計画します。',
        note: '各メーカー認定工法に準拠した施工計画書を作成します。',
      },
      {
        step: '03',
        title: '墨出し・基準線設定',
        desc: 'レーザーレベルを使用し、天井高・壁位置の基準墨を正確に出します。',
        note: '±1mm以内の精度で墨出しを行い、後工程の品質を確保します。',
      },
      {
        step: '04',
        title: 'アンカー打設・インサート確認',
        desc: '構造体へのアンカー打設またはインサートの位置・強度を確認します。',
        note: '耐震天井工法では所定の引抜強度試験を実施します。',
      },
      {
        step: '05',
        title: 'ハンガー・野縁受け取り付け',
        desc: 'ハンガーボルトを所定ピッチで取り付け、野縁受けをレベル調整しながら固定します。',
        note: '天井レベルは通り・高さともに±3mm以内に管理します。',
      },
      {
        step: '06',
        title: 'ランナー・スタッド組み立て',
        desc: '壁下地のランナーを床・天井に固定し、スタッドを所定間隔（303mm・455mm）で立て込みます。',
        note: '開口部・コーナー部は補強スタッドを追加し剛性を確保します。',
      },
      {
        step: '07',
        title: '耐震ブレース・補強取り付け',
        desc: '耐震天井工法の場合、斜め部材（ブレース）を規定角度・間隔で取り付けます。',
        note: 'ブレース取り付け後、全数の角度・緊結状態を確認します。',
      },
      {
        step: '08',
        title: '精度検査・次工程引き渡し',
        desc: '通り・レベル・垂直精度を全箇所確認し、検査記録を作成後にボード工事へ引き渡します。',
        note: '施工写真・検査記録を保管し、品質トレーサビリティを確保します。',
      },
    ],
    workPoints: [
      {
        icon: 'ri-focus-3-line',
        title: '墨出し精度の徹底管理',
        detail: 'レーザーレベルを使用し、天井・壁の基準墨を±1mm以内の精度で出します。この精度が後工程のボード・クロス仕上がりに直結するため、最も重要な工程の一つです。',
      },
      {
        icon: 'ri-earthquake-line',
        title: '耐震工法の適切な選定',
        detail: '建物用途・天井高・重量に応じて各メーカー認定の耐震天井工法を選定します。ブレース角度・間隔・緊結方法を設計図通りに施工し、地震時の安全性を確保します。',
      },
      {
        icon: 'ri-tools-line',
        title: '開口部・設備との取り合い',
        detail: '照明・空調・スプリンクラーなど設備機器の開口部周囲は、補強下地を追加して剛性を確保します。設備業者との事前調整が仕上がり品質を左右します。',
      },
      {
        icon: 'ri-refresh-line',
        title: 'R仕様（曲面）の加工技術',
        detail: '曲面天井・曲面壁は、スタッドに切り込みを入れて曲げ加工を行います。曲率に応じた切り込み間隔の計算と、均一な曲げ精度が美しい仕上がりの鍵です。',
      },
    ],
    faq: [
      { q: 'LGS工事と木下地工事の違いは何ですか？', a: 'LGSは不燃材料のため耐火性能が高く、寸法精度が安定しています。木下地に比べ施工期間の短縮や品質の均一化が図れます。' },
      { q: '耐震天井工法とは何ですか？', a: '地震時に天井が落下しないよう、ブレースや特殊金具を用いて天井を補強する工法です。各メーカーの認定工法に対応しています。' },
      { q: 'R仕様（曲面）の施工は難しいですか？', a: '高い技術と経験が必要ですが、株式会社永恒では豊富な施工実績があり、複雑な曲面にも対応可能です。' },
      { q: 'ボード工事と合わせて依頼できますか？', a: 'はい、LGS下地工事からボード張り・クロス工事まで一貫して承ります。工期短縮とコスト削減が可能です。' },
    ],
  },
  {
    slug: 'board',
    icon: 'ri-layout-masonry-line',
    title: 'ボード工事',
    shortDesc: '石膏ボード・岩綿ジプトーン・化粧板など各種内装用ボード張り施工。耐火・遮音・意匠性を兼ね備えた高品質な内装を実現します。',
    heroImage: 'https://public.readdy.ai/ai/img_res/edited_e44418283fa9fef1c24a320aec5d5bc7_85295ad0.jpg',
    overview: 'ボード工事は、石膏ボード・岩綿ジプトーン・化粧板などの各種内装用ボードを天井・壁面に張り付ける内装仕上工事です。耐火性・遮音性・断熱性に優れた素材を適切に施工することで、安全で快適な空間を実現します。株式会社永恒では、住宅から大型商業施設・公共施設まで、幅広い規模・用途に対応した高品質なボード施工を提供しています。',
    features: [
      { icon: 'ri-fire-line', title: '耐火ボード施工', desc: '耐火性能が求められる建築物に対応した耐火石膏ボードの施工を行います。' },
      { icon: 'ri-volume-mute-line', title: '遮音・防音対応', desc: '遮音ボードや二重張り工法により、高い防音性能を実現します。' },
      { icon: 'ri-grid-line', title: '岩綿ジプトーン施工', desc: '吸音・意匠性に優れた岩綿ジプトーンの天井施工に対応します。' },
      { icon: 'ri-image-line', title: '化粧板・デザインボード', desc: '木目調・石目調など意匠性の高い化粧板施工で空間の質を高めます。' },
      { icon: 'ri-building-4-line', title: '大規模施設対応', desc: 'オフィスビル・病院・学校・商業施設など大規模物件の施工実績多数。' },
      { icon: 'ri-tools-line', title: '複合工法対応', desc: 'LGS下地工事との組み合わせによる一貫施工で工期短縮を実現します。' },
    ],
    workImages: [
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20male%20construction%20workers%20in%20yellow%20safety%20helmets%20and%20blue%20work%20uniforms%20screwing%20gypsum%20plasterboard%20panels%20onto%20metal%20ceiling%20frame%20inside%20large%20office%20building%20interior%20renovation%20site%20bright%20lighting%20professional%20team%20China%20construction&width=800&height=600&seq=board-cn-new-2501&orientation=landscape', caption: 'オフィスビル — 石膏ボード天井施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20male%20workers%20in%20orange%20safety%20helmets%20and%20work%20clothes%20installing%20mineral%20fiber%20rock%20wool%20acoustic%20ceiling%20tiles%20onto%20grid%20system%20inside%20commercial%20building%20interior%20professional%20construction%20team%20China%20renovation%20site%20clean%20workspace&width=800&height=600&seq=board-cn-new-2502&orientation=landscape', caption: '商業施設 — 岩綿ジプトーン天井施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20craftsman%20in%20work%20uniform%20carefully%20fitting%20premium%20decorative%20wood%20grain%20wall%20panels%20in%20hotel%20lobby%20interior%20renovation%20Asian%20male%20worker%20using%20professional%20tools%20elegant%20warm%20lighting%20sophisticated%20interior%20finishing%20China&width=800&height=600&seq=board-cn-new-2503&orientation=landscape', caption: 'ホテルロビー — 化粧板壁面施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20male%20construction%20workers%20in%20white%20safety%20helmets%20screwing%20fire%20resistant%20gypsum%20board%20to%20metal%20stud%20wall%20frame%20inside%20hospital%20medical%20facility%20interior%20renovation%20clean%20bright%20environment%20professional%20team%20China%20construction%20site&width=800&height=600&seq=board-cn-new-2504&orientation=landscape', caption: '医療施設 — 耐火ボード施工' },
    ],
    process: [
      {
        step: '01',
        title: '現地調査・要件確認',
        desc: '施工箇所の下地状況・用途・耐火・遮音などの性能要件を確認します。',
        note: '既存下地の不陸・損傷を事前に把握し、補修計画を立てます。',
      },
      {
        step: '02',
        title: 'ボード種別選定・割付計画',
        desc: '耐火・遮音・意匠など要件に合わせた最適なボードを選定し、割付図を作成します。',
        note: '開口部・コーナー・設備位置を考慮した割付で廃材を最小化します。',
      },
      {
        step: '03',
        title: '下地確認・補強',
        desc: 'LGS下地または木下地の通り・レベル・固定状態を確認し、必要に応じて補強します。',
        note: '下地の不具合を見逃すと仕上がりに影響するため、全数確認を徹底します。',
      },
      {
        step: '04',
        title: 'ボード切断・加工',
        desc: '割付図に基づきボードを正確に切断し、開口部・コーナー部の加工を行います。',
        note: '切断面はカッターで整え、粉塵対策として集塵機を使用します。',
      },
      {
        step: '05',
        title: 'ボード張り・ビス固定',
        desc: '所定のビスピッチ（150mm以下）でボードを確実に固定します。二重張りの場合は目地をずらして張ります。',
        note: 'ビスの沈み込み量（0.5〜1mm）を均一に管理し、ボード破損を防ぎます。',
      },
      {
        step: '06',
        title: 'ジョイント・コーナー処理',
        desc: 'ボードの継ぎ目にジョイントテープを貼り、パテで平滑に仕上げます。コーナービードで角を補強します。',
        note: 'パテは2〜3回に分けて塗り重ね、乾燥後にサンディングで平滑に仕上げます。',
      },
      {
        step: '07',
        title: '精度検査・引き渡し',
        desc: '平滑度・ビス頭の処理・開口部の精度を全数確認後、清掃を行い次工程へ引き渡します。',
        note: '2mストレートエッジで平滑度±3mm以内を確認します。',
      },
    ],
    workPoints: [
      {
        icon: 'ri-layout-masonry-line',
        title: 'ボード割付の最適化',
        detail: '開口部・コーナー・設備位置を考慮した割付計画を事前に作成します。継ぎ目が開口部の角に来ないよう配慮することで、クラックの発生を防ぎ、仕上がりの耐久性を高めます。',
      },
      {
        icon: 'ri-stack-line',
        title: '二重張り工法による遮音性向上',
        detail: '遮音性能が求められる壁・天井には、ボードを二重に張る工法を採用します。一層目と二層目の目地をずらして張ることで、音の透過を効果的に抑制します。',
      },
      {
        icon: 'ri-pencil-ruler-2-line',
        title: 'パテ処理の丁寧な仕上げ',
        detail: 'ジョイント部・ビス頭のパテ処理は2〜3回に分けて塗り重ね、各層を十分に乾燥させてからサンディングします。この工程の丁寧さがクロス仕上がりの美しさを決定します。',
      },
      {
        icon: 'ri-fire-line',
        title: '耐火構造の適切な施工',
        detail: '耐火構造認定を取得した工法では、ボードの種類・厚さ・ビスピッチ・目地処理方法が厳密に規定されています。認定工法の仕様を遵守した施工で、所定の耐火性能を確保します。',
      },
    ],
    faq: [
      { q: '石膏ボードと岩綿ジプトーンの違いは何ですか？', a: '石膏ボードは壁・天井の下地や仕上げに広く使われます。岩綿ジプトーンは吸音性・意匠性に優れ、主に天井仕上げ材として使用されます。' },
      { q: '防音性能を高めたい場合はどうすればよいですか？', a: '遮音ボードの使用や二重張り工法、防振吊り金具の採用などにより遮音性能を向上させることができます。' },
      { q: 'LGS工事と同時に依頼できますか？', a: 'はい、LGS下地工事からボード張りまで一貫して施工可能です。工期短縮とコスト削減につながります。' },
      { q: '化粧板の種類はどのくらいありますか？', a: '木目調・石目調・無地など多数のデザインをご用意しています。サンプルをご覧いただきながらお選びいただけます。' },
    ],
  },
  {
    slug: 'wallpaper',
    icon: 'ri-paint-brush-line',
    title: '壁紙クロス工事',
    shortDesc: '住宅・商業施設の壁紙・クロス張り替え・新規施工。豊富な素材と熟練の技術で美しい空間を実現します。',
    heroImage: 'https://public.readdy.ai/ai/img_res/edited_62c421128908735c9bf7caf8c775f998_cad3331f.jpg',
    overview: '壁紙クロス工事は、住宅・マンション・オフィス・商業施設など、あらゆる空間の壁面・天井面に壁紙（クロス）を張る内装仕上工事です。株式会社永恒では、豊富な素材知識と熟練した職人技術により、お客様のご要望に合わせた最適な壁紙選定から施工まで一貫してご提供します。ビニールクロス・織物クロス・紙クロス・珪藻土クロスなど多様な素材に対応し、新築・リフォームどちらにも対応可能です。',
    features: [
      { icon: 'ri-home-4-line', title: '住宅・マンション対応', desc: '新築・リフォームを問わず、居室・廊下・トイレ・洗面室など全室対応可能です。' },
      { icon: 'ri-building-line', title: 'オフィス・商業施設', desc: 'オフィスビル・店舗・ホテルなど商業空間の大規模施工にも対応します。' },
      { icon: 'ri-palette-line', title: '豊富な素材ラインナップ', desc: 'ビニール・織物・紙・珪藻土・消臭・防カビなど機能性クロスも多数取り扱い。' },
      { icon: 'ri-shield-check-line', title: '丁寧な下地処理', desc: '下地の補修・パテ処理を徹底し、仕上がりの美しさと耐久性を確保します。' },
      { icon: 'ri-time-line', title: '短工期・低騒音', desc: '居住しながらの施工にも配慮し、短期間・低騒音での工事を実現します。' },
      { icon: 'ri-customer-service-2-line', title: 'アフターサポート', desc: '施工後の剥がれ・浮きなどにも迅速に対応するアフターサービスを提供します。' },
    ],
    workImages: [
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20craftsman%20worker%20carefully%20applying%20wallpaper%20on%20interior%20wall%20residential%20renovation%20site%20Asian%20male%20worker%20in%20work%20uniform%20pasting%20wallpaper%20with%20professional%20tools%20bright%20clean%20room%20warm%20lighting%20high%20quality%20interior%20finishing%20work&width=800&height=600&seq=wp-work-cn-001&orientation=landscape', caption: '住宅リビング — ビニールクロス施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20construction%20worker%20installing%20wallpaper%20in%20modern%20office%20building%20interior%20renovation%20Asian%20male%20worker%20in%20safety%20uniform%20smoothing%20wall%20covering%20with%20brush%20professional%20tools%20commercial%20space%20bright%20lighting%20clean%20workspace&width=800&height=600&seq=wp-work-cn-002&orientation=landscape', caption: 'オフィスビル — 機能性クロス施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20craftsman%20applying%20luxury%20patterned%20wallpaper%20in%20hotel%20corridor%20hallway%20Asian%20male%20worker%20in%20work%20clothes%20carefully%20aligning%20wallpaper%20seams%20professional%20interior%20finishing%20warm%20elegant%20lighting%20high%20end%20hospitality%20renovation&width=800&height=600&seq=wp-work-cn-003&orientation=landscape', caption: 'ホテル廊下 — 織物クロス施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20interior%20worker%20installing%20wallpaper%20in%20apartment%20bedroom%20renovation%20Asian%20male%20craftsman%20smoothing%20wall%20covering%20with%20tools%20clean%20bright%20residential%20room%20professional%20finishing%20work%20neat%20uniform%20quality%20interior%20renovation&width=800&height=600&seq=wp-work-cn-004&orientation=landscape', caption: 'マンション寝室 — 消臭クロス施工' },
    ],
    process: [
      {
        step: '01',
        title: '現地調査・ヒアリング',
        desc: '施工箇所の面積・下地状況・既存クロスの状態を確認し、お客様のご要望をヒアリングします。',
        note: '既存クロスの浮き・カビ・下地損傷を事前に把握し、補修範囲を確定します。',
      },
      {
        step: '02',
        title: 'クロス選定・お見積り',
        desc: '用途・デザイン・機能（消臭・防カビ・吸音等）・予算に合わせたクロスをご提案します。',
        note: 'サンプルブックをご覧いただきながら、実際の色味・質感を確認してご選定いただけます。',
      },
      {
        step: '03',
        title: '既存クロス撤去・清掃',
        desc: '既存クロスを丁寧に撤去し、残った糊・汚れを除去して下地面を清潔にします。',
        note: '撤去時に下地を傷つけないよう、スチームを使用して慎重に剥がします。',
      },
      {
        step: '04',
        title: '下地補修・パテ処理',
        desc: 'ビス穴・クラック・不陸をパテで補修し、全面にシーラーを塗布して下地を整えます。',
        note: 'パテは2〜3回塗り重ね、乾燥後にサンディングで平滑度を確保します。この工程が仕上がりの美しさを決定します。',
      },
      {
        step: '05',
        title: 'クロス割付・糊付け',
        desc: '柄合わせ・継ぎ目位置を計画し、糊付け機でクロスに均一に糊を塗布します。',
        note: '糊付け後は適切なオープンタイム（5〜10分）を置き、クロスを十分に伸ばします。',
      },
      {
        step: '06',
        title: 'クロス張り・継ぎ目処理',
        desc: '上から下へ空気を抜きながら張り付け、継ぎ目はダブルカットで美しく仕上げます。',
        note: 'コーナー・開口部周りは特に丁寧に処理し、浮き・しわが出ないよう仕上げます。',
      },
      {
        step: '07',
        title: '乾燥確認・仕上げ検査',
        desc: '施工後24時間以上乾燥させ、浮き・しわ・継ぎ目の状態を全面確認します。',
        note: '不具合箇所は乾燥前に補修し、完全乾燥後に最終検査を行います。',
      },
    ],
    workPoints: [
      {
        icon: 'ri-brush-line',
        title: '下地処理が仕上がりを決める',
        detail: 'クロス工事の品質は下地処理で9割が決まります。ビス穴・クラック・不陸を丁寧にパテ処理し、シーラーで下地を安定させることで、クロスの密着性と仕上がりの美しさが格段に向上します。',
      },
      {
        icon: 'ri-scissors-cut-line',
        title: 'ダブルカットによる継ぎ目処理',
        detail: '隣り合うクロスを重ねて張り、専用カッターで同時に切断するダブルカット工法を採用します。この技法により継ぎ目が目立たない美しい仕上がりを実現します。熟練職人の技術が光る工程です。',
      },
      {
        icon: 'ri-temp-hot-line',
        title: '適切なオープンタイム管理',
        detail: '糊付け後のクロスは適切なオープンタイム（5〜10分）を置くことで、クロスが十分に伸び、張り付け時のしわや浮きを防ぎます。気温・湿度に応じてオープンタイムを調整します。',
      },
      {
        icon: 'ri-palette-line',
        title: '柄合わせの精密な計画',
        detail: '柄物クロスは事前に割付計画を立て、目立つ場所（正面壁・入口正面）で柄が合うよう施工します。リピート（柄の繰り返し間隔）を考慮した材料発注で無駄を最小化します。',
      },
    ],
    faq: [
      { q: '施工期間はどのくらいかかりますか？', a: '一般的な住宅（3LDK程度）で2〜4日が目安です。広さや素材により異なります。' },
      { q: '居住しながら施工できますか？', a: 'はい、可能です。工程を調整し、生活への影響を最小限に抑えて施工します。' },
      { q: '壁紙の種類はどう選べばよいですか？', a: '用途・場所・ご予算に応じてご提案します。サンプルをご覧いただきながら選定できます。' },
      { q: '古い壁紙の上から張ることはできますか？', a: '状態によっては可能ですが、仕上がりの品質のため既存クロスの撤去をお勧めする場合があります。' },
    ],
  },
  {
    slug: 'floor',
    icon: 'ri-layout-bottom-line',
    title: '床仕上工事',
    shortDesc: 'フローリング・タイル・カーペット・長尺シートなど各種床材の施工。住宅から商業施設まで、美しく耐久性の高い床仕上げを提供します。',
    heroImage: 'https://readdy.ai/api/search-image?query=Asian%20Chinese%20male%20construction%20worker%20wearing%20orange%20safety%20helmet%20and%20grey%20work%20uniform%20squatting%20down%20precisely%20installing%20light%20oak%20hardwood%20floor%20planks%20in%20bright%20newly%20built%20apartment%20room%20interior%20warm%20sunlight%20professional%20flooring%20installation%20tools%20China%20domestic%20renovation%20site&width=1200&height=600&seq=floor-hero-cn-v9-alpha&orientation=landscape',
    overview: '床仕上工事は、フローリング・フロアタイル・カーペット・長尺シート・置き床など、各種床材を施工する内装仕上工事です。床は空間の印象を大きく左右する重要な要素であり、素材の選定から丁寧な施工まで、株式会社永恒では高品質な床仕上げをご提供します。住宅・マンション・オフィス・商業施設・医療福祉施設など、あらゆる用途・規模に対応可能です。',
    features: [
      { icon: 'ri-home-4-line', title: 'フローリング施工', desc: '無垢材・複合フローリングなど、住宅・マンションの床材施工に幅広く対応します。' },
      { icon: 'ri-grid-line', title: 'フロアタイル・Pタイル', desc: 'オフィス・商業施設向けのフロアタイル・Pタイルの施工を行います。' },
      { icon: 'ri-layout-bottom-2-line', title: '長尺シート施工', desc: '医療・福祉施設や商業施設に適した長尺シートの施工に対応します。' },
      { icon: 'ri-sofa-line', title: 'カーペット施工', desc: 'ホテル・オフィス・住宅向けのタイルカーペット・グリッパー工法に対応します。' },
      { icon: 'ri-building-4-line', title: '置き床・OAフロア', desc: 'オフィスのOAフロアや置き床工法による床下地の施工も承ります。' },
      { icon: 'ri-shield-check-line', title: '下地補修・レベル調整', desc: '既存床の撤去・下地補修・レベル調整を丁寧に行い、美しい仕上がりを実現します。' },
    ],
    workImages: [
      { src: 'https://public.readdy.ai/ai/img_res/edited_80ffeaa6ebfa1fc234ec9cc8a55f09be_1b0071f2.jpg', caption: 'マンション — フローリング施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20male%20construction%20worker%20wearing%20red%20safety%20helmet%20and%20grey%20work%20uniform%20kneeling%20on%20floor%20carefully%20unrolling%20and%20smoothing%20grey%20vinyl%20long%20sheet%20flooring%20in%20bright%20hospital%20corridor%20medical%20facility%20interior%20professional%20tools%20neat%20clean%20environment%20China%20domestic%20renovation%20site%20realistic%20photo&width=800&height=600&seq=floor-sheet-cn-v12-001&orientation=landscape', caption: '医療施設 — 長尺シート施工' },
      { src: 'https://readdy.ai/api/search-image?query=Chinese%20male%20craftsman%20wearing%20yellow%20safety%20helmet%20and%20dark%20navy%20work%20uniform%20kneeling%20on%20floor%20precisely%20placing%20and%20pressing%20square%20carpet%20tile%20in%20luxury%20hotel%20room%20warm%20interior%20lighting%20elegant%20decor%20professional%20flooring%20installation%20China%20renovation%20site%20realistic%20photo&width=800&height=600&seq=floor-carpet-cn-v12-002&orientation=landscape', caption: 'ホテル客室 — タイルカーペット施工' },
    ],
    process: [
      {
        step: '01',
        title: '現地調査・下地確認',
        desc: '既存床の状態・下地の種類（コンクリート・合板等）・不陸・含水率を測定します。',
        note: '含水率が高い場合は乾燥期間を設けるか、防湿処理を行います。',
      },
      {
        step: '02',
        title: '床材選定・割付計画',
        desc: '用途・デザイン・機能（防滑・抗菌・防音等）に合わせた床材を選定し、割付図を作成します。',
        note: '部屋の中心から割り付けることで、端部の床材が均等になり美しく仕上がります。',
      },
      {
        step: '03',
        title: '既存床撤去・清掃',
        desc: '既存床材を撤去し、残った接着剤・汚れを除去して下地面を清潔にします。',
        note: '撤去時に下地を傷つけないよう、適切な工具を使用して慎重に作業します。',
      },
      {
        step: '04',
        title: '下地補修・レベル調整',
        desc: 'クラック・段差・不陸をセルフレベリング材またはパテで補修し、平滑な下地を作ります。',
        note: '3mストレートエッジで±3mm以内の平滑度を確認してから次工程に進みます。',
      },
      {
        step: '05',
        title: 'プライマー塗布・接着剤塗布',
        desc: '下地の吸水調整のためプライマーを塗布後、床材に適した接着剤をクシ目ゴテで均一に塗布します。',
        note: '接着剤のオープンタイムを守ることで、適切な接着強度が得られます。',
      },
      {
        step: '06',
        title: '床材施工・圧着',
        desc: '割付図に従い床材を正確に配置し、ローラーで全面を圧着して接着剤を密着させます。',
        note: 'フローリングは雄実・雌実を確実に嵌合させ、ゴムハンマーで叩き込みます。',
      },
      {
        step: '07',
        title: '養生・仕上げ検査',
        desc: '施工後24〜48時間の養生期間を設け、接着剤が完全に硬化してから最終検査を行います。',
        note: '浮き・剥がれ・段差・目地の均一性を全面確認し、不具合箇所を補修します。',
      },
    ],
    workPoints: [
      {
        icon: 'ri-drop-line',
        title: '含水率管理が品質の鍵',
        detail: 'コンクリート下地の含水率が高い状態で施工すると、接着不良・膨れ・カビの原因になります。施工前に含水率計で測定し、規定値（8%以下）を確認してから施工を開始します。',
      },
      {
        icon: 'ri-ruler-line',
        title: '割付計画による美しい仕上がり',
        detail: '床材の割付は部屋の中心から計画し、出入口・窓側など目立つ場所で床材が均等になるよう配慮します。事前の割付計画により、端部の細切れを防ぎ、空間全体の美しさを高めます。',
      },
      {
        icon: 'ri-temp-cold-line',
        title: '接着剤のオープンタイム管理',
        detail: '接着剤は塗布後、適切なオープンタイム（気温・湿度により5〜20分）を置いてから床材を貼ります。オープンタイムが短すぎると接着不良、長すぎると接着剤が硬化して密着しません。',
      },
      {
        icon: 'ri-shield-check-line',
        title: '養生による接着強度の確保',
        detail: '施工後は24〜48時間の養生期間を設け、重量物の搬入や歩行を制限します。この養生期間中に接着剤が完全に硬化し、長期にわたる耐久性が確保されます。',
      },
    ],
    faq: [
      { q: 'フローリングの種類はどう選べばよいですか？', a: '用途・場所・ご予算に応じてご提案します。無垢材・複合フローリングなどサンプルをご覧いただきながら選定できます。' },
      { q: '既存の床の上から施工できますか？', a: '状態によっては可能ですが、仕上がりの品質のため既存床材の撤去をお勧めする場合があります。' },
      { q: '医療・福祉施設向けの床材はありますか？', a: 'はい、抗菌・防滑機能を持つ長尺シートなど、医療・福祉施設に適した床材を多数取り扱っています。' },
      { q: 'LGS工事・ボード工事と合わせて依頼できますか？', a: 'はい、内装仕上工事を一貫して承ります。工期短縮とコスト削減が可能です。' },
    ],
  },
];
